import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";

const MATTERS = [
  ["patentability", "Patentability / prior-art search"],
  ["fto", "Freedom-to-operate (FTO) study"],
  ["drafting", "Patent drafting"],
  ["invalidity", "Patent invalidity search"],
  ["landscape", "Patent landscape analysis"],
  ["valuation", "Patent valuation"],
  ["trademark", "Trademark search & filing"],
  ["other", "Other"],
];

const inputCls =
  "w-full h-11 px-4 bg-transparent border border-[#C6A87C]/25 text-[#F5F4F0] placeholder:text-[#8A99AA]/50 focus:border-[#C6A87C] outline-none transition-colors duration-300 text-sm";

export default function EnquiryForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    matter_type: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.matter_type || !form.description) {
      toast.error("Please fill your name, email, phone, type of matter and a brief description.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. You'll hear back within one business day.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", matter_type: "", description: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        data-testid="enquiry-success"
        className="border border-[#0F9D6B]/50 bg-navy-deep p-10 text-center"
      >
        <div className="label-overline mb-3">Received</div>
        <h3 className="text-3xl text-[#F5F4F0] mb-3 font-serif">Thank you — your enquiry is in.</h3>
        <p className="text-[#8A99AA] font-light max-w-md mx-auto">
          Venkataramanan will personally review your matter and respond within one business
          day with a free 15-minute review.
        </p>
        <button
          data-testid="enquiry-submit-another"
          onClick={() => setSubmitted(false)}
          className="mt-6 h-11 px-8 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.14em] uppercase hover:bg-[#14B981] transition-colors duration-300"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <Reveal>
      <form
        data-testid="enquiry-form"
        onSubmit={onSubmit}
        className="bg-navy-deep border border-[#C6A87C]/20 p-8 lg:p-10 space-y-6"
      >
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Name" required>
            <input
              data-testid="enquiry-name"
              value={form.name}
              onChange={update("name")}
              placeholder="e.g. Aanya Iyer"
              className={inputCls}
            />
          </Field>
          <Field label="Email" required>
            <input
              data-testid="enquiry-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Phone" required>
          <input
            data-testid="enquiry-phone"
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="+91 ..."
            className={inputCls}
          />
        </Field>

        <Field label="Type of matter" required>
          <Select value={form.matter_type} onValueChange={update("matter_type")}>
            <SelectTrigger
              data-testid="enquiry-matter-select"
              className="w-full h-11 px-4 rounded-none bg-transparent border-[#C6A87C]/25 text-[#F5F4F0] focus:border-[#C6A87C] focus:ring-0 text-sm"
            >
              <SelectValue placeholder="Select the type of matter" />
            </SelectTrigger>
            <SelectContent className="bg-[#0B2038] border-[#C6A87C]/25 text-[#F5F4F0] rounded-none">
              {MATTERS.map(([value, label]) => (
                <SelectItem
                  key={value}
                  value={value}
                  data-testid={`enquiry-option-${value}`}
                  className="focus:bg-[#C6A87C] focus:text-[#061324] cursor-pointer"
                >
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Brief description" required>
          <textarea
            data-testid="enquiry-description"
            value={form.description}
            onChange={update("description")}
            placeholder="Tell us what you're working on — invention summary, jurisdictions, timelines..."
            rows={5}
            className={`${inputCls} h-auto py-3 resize-y`}
          />
        </Field>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
          <button
            data-testid="enquiry-submit"
            type="submit"
            disabled={submitting}
            className="group relative inline-flex items-center gap-3 h-12 px-10 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden disabled:opacity-50"
          >
            <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
            <span className="relative inline-flex items-center gap-2">
              {submitting ? "Sending..." : "Brief us on your matter"}
              <ArrowRight size={14} weight="bold" />
            </span>
          </button>
          <p className="text-xs text-[#8A99AA]/70 font-light">
            Free 15-minute review from a CPVA. No payment required to enquire.
          </p>
        </div>
      </form>
    </Reveal>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#8A99AA] font-semibold">
        {label} {required && <span className="text-[#C6A87C]">*</span>}
      </label>
      {children}
    </div>
  );
}
